import { LOGO_SRC } from '../components/LenticLogo'

/**
 * Builds a favicon from the official Lentic logo file by isolating the circular
 * ripple symbol and dropping the wordmark.
 *
 * The symbol is never redrawn: the source SVG is fetched, the wordmark shapes
 * are removed, and the viewBox is tightened to the symbol, so its geometry,
 * proportions and colours are exactly those of the official asset.
 */

type Box = { x: number; y: number; width: number; height: number }

const SHAPE_SELECTOR = 'path, circle, ellipse, rect, polygon, polyline, line'

function parseViewBox(svg: SVGSVGElement): Box | null {
  const raw = svg.getAttribute('viewBox')
  if (!raw) return null
  const [x, y, width, height] = raw.trim().split(/[\s,]+/).map(Number)
  if ([x, y, width, height].some((n) => Number.isNaN(n))) return null
  return { x, y, width, height }
}

/**
 * Splits the shapes at the widest horizontal gap. In a horizontal lockup that
 * gap is the space between the symbol and the wordmark, so everything left of
 * it is the symbol.
 */
function symbolIndices(rects: DOMRect[], containerWidth: number): number[] {
  const order = rects
    .map((rect, index) => ({ index, left: rect.left, right: rect.right }))
    .sort((a, b) => a.left - b.left)

  let sweepRight = order[0]?.right ?? 0
  let splitAt = order.length
  let widestGap = 0

  for (let i = 1; i < order.length; i += 1) {
    const gap = order[i].left - sweepRight
    if (gap > widestGap && gap > containerWidth * 0.02) {
      widestGap = gap
      splitAt = i
    }
    sweepRight = Math.max(sweepRight, order[i].right)
  }

  return order.slice(0, splitAt).map((entry) => entry.index)
}

export async function buildSymbolFavicon(): Promise<string | null> {
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') return null

  try {
    const response = await fetch(LOGO_SRC)
    if (!response.ok) return null

    const source = await response.text()
    const parsed = new DOMParser().parseFromString(source, 'image/svg+xml')
    const root = parsed.documentElement
    if (!root || root.nodeName.toLowerCase() !== 'svg') return null

    const live = document.importNode(root, true) as unknown as SVGSVGElement
    const viewBox = parseViewBox(live)
    if (!viewBox) return null

    // Measure offscreen: rendered layout is required for element geometry.
    live.setAttribute('width', String(viewBox.width))
    live.setAttribute('height', String(viewBox.height))
    live.style.position = 'absolute'
    live.style.left = '-10000px'
    live.style.top = '0'
    live.style.opacity = '0'
    live.style.pointerEvents = 'none'
    document.body.appendChild(live)

    const shapes = Array.from(live.querySelectorAll<SVGGraphicsElement>(SHAPE_SELECTOR))
    if (shapes.length === 0) {
      document.body.removeChild(live)
      return null
    }

    const containerRect = live.getBoundingClientRect()
    const rects = shapes.map((shape) => shape.getBoundingClientRect())
    const keep = new Set(symbolIndices(rects, containerRect.width))

    // Union of the kept shapes, in viewBox units.
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    const scaleX = viewBox.width / containerRect.width
    const scaleY = viewBox.height / containerRect.height

    keep.forEach((index) => {
      const rect = rects[index]
      minX = Math.min(minX, viewBox.x + (rect.left - containerRect.left) * scaleX)
      maxX = Math.max(maxX, viewBox.x + (rect.right - containerRect.left) * scaleX)
      minY = Math.min(minY, viewBox.y + (rect.top - containerRect.top) * scaleY)
      maxY = Math.max(maxY, viewBox.y + (rect.bottom - containerRect.top) * scaleY)
    })

    document.body.removeChild(live)

    if (![minX, minY, maxX, maxY].every(Number.isFinite)) return null

    // Rebuild from a clean clone so ancestor transforms survive intact.
    const clone = document.importNode(root, true) as unknown as SVGSVGElement
    const cloneShapes = Array.from(
      clone.querySelectorAll<SVGGraphicsElement>(SHAPE_SELECTOR),
    )
    cloneShapes.forEach((shape, index) => {
      if (!keep.has(index)) shape.parentNode?.removeChild(shape)
    })

    // Square canvas with a little breathing room, so nothing is distorted.
    const width = maxX - minX
    const height = maxY - minY
    const side = Math.max(width, height) * 1.08
    const boxX = minX - (side - width) / 2
    const boxY = minY - (side - height) / 2

    clone.setAttribute('viewBox', `${boxX} ${boxY} ${side} ${side}`)
    clone.setAttribute('width', '64')
    clone.setAttribute('height', '64')
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
    clone.removeAttribute('style')

    const markup = new XMLSerializer().serializeToString(clone)
    return `data:image/svg+xml,${encodeURIComponent(markup)}`
  } catch {
    return null
  }
}
